import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

export type UpdateWebsiteLandingTypeParams =
  | { websiteId: string; landingType: 'in-app' }
  | { websiteId: string; landingType: 'external'; externalHomepageUrl: string };

function updateWebsiteLandingType(params: UpdateWebsiteLandingTypeParams) {
  const body =
    params.landingType === 'external'
      ? { landingType: params.landingType, externalHomepageUrl: params.externalHomepageUrl }
      : { landingType: params.landingType };

  return axiosPrivate
    .put(`/v3/pharmacist/plus-websites/${params.websiteId}/landing-types`, body)
    .then((r) => r.data);
}

export function useUpdateWebsiteLandingType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWebsiteLandingType,
    // Mirrors useUpdateWebsiteExternalHomepageUrl: the list endpoint can lag
    // behind this PUT, so the accepted response is written straight into the
    // cache instead of triggering a refetch that may return the stale value.
    onSuccess: (_data, params: UpdateWebsiteLandingTypeParams) => {
      const { landingType } = params;
      const externalHomepageUrl =
        params.landingType === 'external' ? params.externalHomepageUrl : null;

      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>(
        { queryKey: ['plus-websites'] },
        (old) =>
          old
            ? {
                ...old,
                data: {
                  ...old.data,
                  plusWebsites: old.data.plusWebsites.map((website) =>
                    website.id === params.websiteId
                      ? { ...website, landingType, externalHomepageUrl }
                      : website
                  ),
                },
              }
            : old
      );
    },
    meta: {
      successMessage: 'Home page updated successfully',
      errorMessage: 'Failed to update home page',
    },
  });
}
